import React, { useMemo } from 'react';
import {
  Avatar,
  Badge,
  Box,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { theme } from '../../../../theme';

const formatConversationDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
};

const MessagingConversationList = ({
  conversations = [],
  selectedId,
  query = '',
  onQueryChange,
  onSelect,
  getIdentity,
  getPreview,
  emptyTitle,
  emptyDescription,
}) => {
  const visible = useMemo(() => (
    [...conversations]
      .filter((conversation) => {
        const identity = getIdentity(conversation) || '';
        const preview = getPreview(conversation) || '';
        const haystack = [
          identity,
          preview,
          conversation.subject,
          conversation.kind,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(query.trim().toLowerCase());
      })
      .sort((left, right) => (
        new Date(right.last_message_at || right.updated_at || 0)
        - new Date(left.last_message_at || left.updated_at || 0)
      ))
  ), [conversations, getIdentity, getPreview, query]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.76)',
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.25,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, color: theme.colors.secondary[950] }}
          >
            Conversations
          </Typography>
          {!!conversations.length && (
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.35,
                borderRadius: 5,
                backgroundColor: theme.colors.primary[100],
                color: theme.colors.primary[900],
                fontWeight: 800,
              }}
            >
              {conversations.length}
            </Typography>
          )}
        </Box>
        <TextField
          fullWidth
          size="small"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Rechercher"
          inputProps={{ 'aria-label': 'Rechercher une discussion' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon
                  fontSize="small"
                  sx={{ color: theme.colors.primary[800] }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              backgroundColor: theme.colors.secondary[50],
              '& fieldset': {
                borderColor: theme.colors.secondary[200],
              },
              '&:hover fieldset, &.Mui-focused fieldset': {
                borderColor: theme.colors.primary[600],
              },
            },
          }}
        />
      </Box>

      <List
        aria-label="Liste des discussions"
        disablePadding
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.25, pb: 1.5 }}
      >
        {visible.map((conversation) => {
          const identity = getIdentity(conversation) || 'Conversation';
          const preview = getPreview(conversation) || 'Nouvelle discussion';
          const unread = Number(conversation.unread_count || 0);
          const selected = Number(selectedId) === Number(conversation.id);
          const avatar = (
            conversation.salon?.profile_photo
            || conversation.salon?.logo
            || conversation.client?.profile_photo
            || conversation.client?.avatar
          );
          return (
            <ListItemButton
              key={conversation.id}
              selected={selected}
              aria-label={
                unread
                  ? `${identity}, ${unread} message${unread > 1 ? 's' : ''} non lu${unread > 1 ? 's' : ''}`
                  : identity
              }
              onClick={() => onSelect(conversation)}
              sx={{
                mb: 0.75,
                px: 1.25,
                py: 1.1,
                borderRadius: '16px',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: theme.colors.primary[50],
                },
                '&.Mui-selected, &.Mui-selected:hover': {
                  backgroundColor: theme.colors.primary[100],
                  borderColor: theme.colors.primary[200],
                },
              }}
            >
              <ListItemAvatar sx={{ minWidth: 52 }}>
                <Badge
                  badgeContent={unread}
                  color="error"
                  invisible={!unread}
                  overlap="circular"
                >
                  <Avatar
                    src={avatar || undefined}
                    alt=""
                    sx={{
                      width: 42,
                      height: 42,
                      backgroundColor: theme.colors.secondary[300],
                      color: theme.colors.secondary[950],
                      fontWeight: 800,
                    }}
                  >
                    {identity.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={(
                  <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      variant="body2"
                      sx={{ flex: 1, fontWeight: unread ? 800 : 700 }}
                    >
                      {identity}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.colors.grey[500], flexShrink: 0 }}
                    >
                      {formatConversationDate(
                        conversation.last_message_at
                        || conversation.updated_at,
                      )}
                    </Typography>
                  </Box>
                )}
                secondary={preview}
                secondaryTypographyProps={{
                  noWrap: true,
                  sx: {
                    mt: 0.35,
                    color: unread
                      ? theme.colors.grey[700]
                      : theme.colors.grey[600],
                    fontWeight: unread ? 600 : 400,
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {!visible.length && (
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            flex: 1,
            px: 3,
            pb: 5,
            textAlign: 'center',
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 58,
                height: 58,
                mx: 'auto',
                mb: 1.5,
                borderRadius: '18px',
                backgroundColor: theme.colors.primary[100],
                color: theme.colors.primary[900],
              }}
            >
              <ChatBubbleOutlineRoundedIcon />
            </Box>
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
              {query ? 'Aucun résultat' : emptyTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {query
                ? 'Essayez avec un autre nom ou mot-clé.'
                : emptyDescription}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MessagingConversationList;
